import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { wikimediaSized } from "../../src/wikimedia.ts";
import { api, type Item, type TripFile } from "./api.ts";
import { ago, itemIcon, kindClass, kindOf } from "./format.ts";
import { useDrawsHeic } from "./heic.ts";
import { useMouseDrag } from "./mouseDrag.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Icon } from "./souvenir.ts";
import { ItemIcon, KindIcon } from "./ui.tsx";
import { fileHref, viewingCode } from "./viewing.ts";

// Photos (features/photos.md): the one place a photo is drawn, on a card's strip, full screen, and as a
// thumbnail on Ideas and Bookings. Two kinds, the group's own first:
// - Own photos are files added as photos ("Add a photo", or as "photo" through an AI): kept by Tripmo,
//   served from /api/files/<id>, which this browser keeps for when there's no signal. Each says who
//   added it, and can be removed (undoable from Updates).
// - Web photos are references: the browser loads each from where it's published, with no referrer,
//   lazily, at the size it shows at (where the source has sizes). A web photo this page can't load is
//   reported once; Tripmo checks it again itself before calling it broken. Serving them through Tripmo
//   later (a resizing proxy) is a change to `photoSrc` alone.
// While a photo loads, and if it can't, the thing's kind icon shows in the same box, so nothing moves.

export type Photo = NonNullable<Item["photos"]>[number];

// A photo as shown: the group's own (a file Tripmo keeps) or one from the web.
export type Shown = { own: TripFile } | { web: Photo };

const keyOf = (p: Shown) => ("own" in p ? p.own.id : p.web.url);

// Whether this browser shows a file as its item's photo: one added as a photo, in a format it draws
// (JPEG, PNG and WebP everywhere; HEIC only where the browser draws it, Safari). Elsewhere a HEIC photo
// stays in the card's files, to download.
export const showsAsPhoto = (f: TripFile, drawsHeic: boolean | null) =>
  f.as === "photo" && (/^image\/(jpeg|png|webp)$/.test(f.type) || (f.type === "image/heic" && drawsHeic === true));

// The trip's own photos this browser shows, by item, oldest first: given once, for every view.
const OwnContext = createContext<ReadonlyMap<string, TripFile[]>>(new Map());

export function OwnPhotos({ files, children }: { files: TripFile[]; children: ReactNode }) {
  const drawsHeic = useDrawsHeic();
  const byItem = useMemo(() => {
    const out = new Map<string, TripFile[]>();
    for (const f of files) if (showsAsPhoto(f, drawsHeic)) out.set(f.on, [...(out.get(f.on) ?? []), f]);
    return out;
  }, [files, drawsHeic]);
  return <OwnContext.Provider value={byItem}>{children}</OwnContext.Provider>;
}

// An item's photos as shown: the group's own first, then the web's.
export const photosOf = (item: Item, own: readonly TripFile[] = []): Shown[] => [
  ...own.map((f) => ({ own: f })),
  ...(item.photos ?? []).map((p) => ({ web: p })),
];

export function usePhotos(item: Item): Shown[] {
  const own = useContext(OwnContext).get(item.id);
  return photosOf(item, own);
}

// On a shared trip's view link, the view's own address (viewing.ts).
const fileSrc = (f: TripFile) => fileHref(f.id);

// The address to load a photo from, for a box `width` CSS pixels wide on this screen: Wikimedia's in the
// smallest of its standard widths that's sharp there (never larger than the one kept). Any other source,
// as it's kept.
export function photoSrc(url: string, width: number, density = globalThis.devicePixelRatio || 1): string {
  return wikimediaSized(url, width * density) ?? url;
}

// Each photo this page reported, once: a list of thumbnails failing together sends one report each.
const reported = new Set<string>();
function reportBroken(trip: string, item: string, url: string) {
  // Without a connection nothing loads, and nothing is wrong with the photo. A shared trip's viewer tells nobody: the
  // trip's members' AIs hear of it from what the members' pages report.
  if (!navigator.onLine || viewingCode() || reported.has(`${item} ${url}`)) return;
  reported.add(`${item} ${url}`);
  api.photoBroken(trip, item, url).catch(() => reported.delete(`${item} ${url}`));
}

type Load = "loading" | "loaded" | "failed";

// One web photo, over whatever sits under it (the kind icon). Tries the size it asked for, then the
// address as kept, and only then gives up: hidden, and reported.
function WebImage({
  photo,
  item,
  trip,
  width,
  className,
  alt = "",
  onState,
}: {
  photo: Photo;
  item: Item;
  trip: string;
  // How wide it shows, in CSS pixels.
  width: number;
  className: string;
  alt?: string;
  onState?: (state: Load) => void;
}) {
  const sized = photoSrc(photo.url, width);
  const [src, setSrc] = useState(sized);
  const [state, setState] = useState<Load>("loading");
  const ref = useRef<HTMLImageElement>(null);
  const set = (next: Load) => {
    setState(next);
    onState?.(next);
  };
  // Already there (from this browser's cache) before React heard it load.
  // biome-ignore lint/correctness/useExhaustiveDependencies: checked once, for the first address
  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) set("loaded");
  }, []);
  if (state === "failed") return null;
  return (
    <img
      ref={ref}
      className={`${className}${state === "loaded" ? " is-loaded" : ""}`}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={() => set("loaded")}
      onError={() => {
        if (src !== photo.url) return setSrc(photo.url);
        set("failed");
        reportBroken(trip, item.id, photo.url);
      }}
    />
  );
}

// One of the group's own photos, from Tripmo (or, with no signal, what this browser kept of it). One that
// can't load (not kept here, no signal) is hidden, leaving the icon under it.
function OwnImage({
  file,
  className,
  alt = "",
  onState,
}: {
  file: TripFile;
  className: string;
  alt?: string;
  onState?: (state: Load) => void;
}) {
  const [state, setState] = useState<Load>("loading");
  const ref = useRef<HTMLImageElement>(null);
  const set = (next: Load) => {
    setState(next);
    onState?.(next);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: checked once, on the first render
  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) set("loaded");
  }, []);
  if (state === "failed") return null;
  return (
    <img
      ref={ref}
      className={`${className}${state === "loaded" ? " is-loaded" : ""}`}
      src={fileSrc(file)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => set("loaded")}
      onError={() => set("failed")}
    />
  );
}

// Either kind of photo, `width` CSS pixels wide.
function PhotoImage(props: {
  photo: Shown;
  item: Item;
  trip: string;
  width: number;
  className: string;
  alt?: string;
  onState?: (state: Load) => void;
}) {
  const { photo, item, trip, width, className, alt, onState } = props;
  const extra = { ...(alt !== undefined ? { alt } : {}), ...(onState ? { onState } : {}) };
  if ("own" in photo) return <OwnImage file={photo.own} className={className} {...extra} />;
  return <WebImage photo={photo.web} item={item} trip={trip} width={width} className={className} {...extra} />;
}

// A thing's first photo where its kind icon would be: the icon shows while it loads, and stays if it
// can't, or if there's none. In a row, the photo fills the icon's own disc, inside the ring of its kind's
// colour (a suggestion's dashed ring); on an option's card, a larger square (`square`, pixels a side).
export function ItemThumb({
  item,
  trip,
  square,
  iconSize,
  suggested = false,
}: {
  item: Item;
  trip: string;
  square?: number;
  iconSize?: number;
  suggested?: boolean;
}) {
  const photo = usePhotos(item)[0];
  const icon = <KindIcon item={item} suggested={suggested} {...(iconSize ? { size: iconSize } : {})} />;
  if (!photo) return icon;
  return (
    <span
      className={`tm-thumb${suggested ? " is-suggested" : ""}${square ? " is-square" : ""}`}
      style={square ? ({ "--thumb": `${square}px` } as CSSProperties) : undefined}
    >
      {icon}
      <PhotoImage
        key={keyOf(photo)}
        photo={photo}
        item={item}
        trip={trip}
        width={square ?? 32}
        className="tm-thumb-img"
      />
    </span>
  );
}

// A thing's first photo filling the top of a deck's card (Deck.tsx), `width` CSS pixels wide, credited on
// it as on a card's strip: its kind's colour with its icon while it loads, and in its place when it has
// none or it can't load. The credit (a web photo's link) is the one thing on it that takes a tap.
export function ItemCover({ item, trip, width, you }: { item: Item; trip: string; width: number; you: string }) {
  const photo = usePhotos(item)[0];
  const [state, setState] = useState<Load>("loading");
  return (
    <span className={`tm-cover ${kindClass(kindOf(item))} is-${photo ? state : "none"}`}>
      <span className="tm-cover-icon">
        <ItemIcon name={itemIcon(item)} size={40} />
      </span>
      {photo && (
        <>
          <PhotoImage
            key={keyOf(photo)}
            photo={photo}
            item={item}
            trip={trip}
            width={width}
            className="tm-cover-img"
            onState={setState}
          />
          {state === "loaded" &&
            ("own" in photo ? (
              <span className="caption tm-strip-credit tm-strip-by tm-cover-credit">
                <Icon name="camera" size={14} />
                <span className="tm-credit-text">{capital(photo.own.by.id === you ? "you" : photo.own.by.name)}</span>
              </span>
            ) : (
              <Credit photo={photo.web} className="tm-strip-credit tm-cover-credit" />
            ))}
        </>
      )}
    </span>
  );
}

// Who added an own photo: "you", or their name.
type Who = (by: { id: string; name: string }) => string;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// A card's photos, at its top, the group's own first: one fills the width; with more, the next one
// peeks in. On a phone a swipe moves it; on a computer a drag of the mouse, a sideways scroll on the
// trackpad, Previous and Next at its edges (shown on hover and on keyboard focus, hidden at either end)
// and the arrow keys once it has focus. Dots under it say which photo is showing and take no taps or
// clicks (DESIGN.md, "Within a thumb's reach"); a screen reader hears one status, "Photo 2 of 4", once the
// strip settles. Each opens full screen on a tap or a click, never at the end of a drag. A web photo
// credits its source, which opens the page it's from; an own photo says who added it, and can be removed
// full screen.
export function PhotoStrip({
  item,
  trip,
  you,
  onChanged,
}: {
  item: Item;
  trip: string;
  // Who's looking (an own photo of theirs says "You").
  you: string;
  // After an own photo is removed: load the trip again.
  onChanged: () => void;
}) {
  const photos = usePhotos(item);
  const [open, setOpen] = useState<number | null>(null);
  const [showing, setShowing] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const prev = useRef<HTMLButtonElement>(null);
  const next = useRef<HTMLButtonElement>(null);
  // The photo showing when the mouse was pressed on the strip, for where a drag from it settles.
  const from = useRef(0);
  // A drag with the mouse (a phone's touch scrolls the strip itself), never opening the photo it ends on. Snapping
  // is off while it moves, back on once it has eased onto a photo: the next one in the drag's direction, past a
  // short drag.
  const drag = useMouseDrag<HTMLUListElement>({
    enabled: photos.length > 1,
    onPress: (el) => {
      from.current = nearest(el);
    },
    onDrop: (el, dx, done) => {
      let to = nearest(el);
      if (to === from.current && Math.abs(dx) > 40) to = from.current + (dx < 0 ? 1 : -1);
      if (go(to)) return done();
      el.addEventListener("scrollend", done, { once: true });
      setTimeout(done, 700);
    },
  });
  // An eased step on its way (Previous, Next, a key): the photos it passes aren't the one showing, so a
  // second Next pressed while it eases goes on from where the first is headed, not from where it's passing.
  // Each step its own number, never used again: counted afresh from nought once a step arrived, a step's
  // late fallback (below) took a later step with its number for itself, ended it as it was passing and
  // sent the next Next to the photo it was already headed for (2026-10-23, Next, Next, then Next, Next a
  // second after the first: the strip stopped one photo short).
  const steps = useRef(0);
  const easing = useRef(0);
  // The photo the status names: the one showing, once the strip has stayed on it a moment, so a swipe
  // or a scroll past several photos is one announcement, not one per photo it passes.
  const [said, setSaid] = useState(0);
  useEffect(() => {
    const settled = setTimeout(() => setSaid(showing), 400);
    return () => clearTimeout(settled);
  }, [showing]);
  if (photos.length === 0) return null;
  const last = photos.length - 1;
  const at = Math.min(showing, last);
  const several = last > 0;
  const who: Who = (by) => (by.id === you ? "you" : by.name);

  // Where the strip scrolls to for photo i: its start at the card's edge, or as far as the strip goes.
  const leftOf = (el: HTMLUListElement, i: number) => {
    const photo = el.children[i];
    if (!photo) return 0;
    const pad = Number.parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
    const left = photo.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft - pad;
    return Math.max(0, Math.min(el.scrollWidth - el.clientWidth, left));
  };
  // The photo showing: the one whose place the strip is nearest.
  const nearest = (el: HTMLUListElement) => {
    let best = 0;
    for (let i = 1; i <= last; i++)
      if (Math.abs(leftOf(el, i) - el.scrollLeft) < Math.abs(leftOf(el, best) - el.scrollLeft)) best = i;
    return best;
  };
  // Show photo i: eased, or at once for someone who asked their device for less motion. Focus on a
  // button that's about to hide (Next, at the end) moves to the one across, so the keys still work.
  const go = (i: number) => {
    const el = list.current;
    if (!el) return;
    const to = Math.max(0, Math.min(last, i));
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!still) {
      const step = ++steps.current;
      easing.current = step;
      const arrived = () => {
        if (easing.current !== step) return;
        easing.current = 0;
        setShowing(nearest(el));
      };
      el.addEventListener("scrollend", arrived, { once: true });
      // Where the browser has no scrollend, or the strip is already there and never scrolls.
      setTimeout(arrived, 1000);
    }
    el.scrollTo({ left: leftOf(el, to), behavior: still ? "instant" : "smooth" });
    setShowing(to);
    const focused = document.activeElement;
    if (focused === prev.current && to === 0) next.current?.focus();
    else if (focused === next.current && to === last) prev.current?.focus();
    else if (focused instanceof HTMLElement && focused.closest(".tm-strip-photo"))
      // On a photo: onto the one now showing, without the browser jumping to it.
      el.children[to]?.querySelector<HTMLElement>(".tm-strip-open")?.focus({ preventScroll: true });
    return still;
  };

  return (
    <section
      className={`tm-strip${several ? " is-several" : ""}${at === last ? " is-at-end" : ""}`}
      aria-label="Photos"
      onKeyDown={(e) => {
        // Only keys pressed in the strip itself (the viewer, drawn elsewhere, has its own).
        if (!several || open !== null || !e.currentTarget.contains(e.target as Node)) return;
        if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
        const to =
          e.key === "ArrowRight"
            ? at + 1
            : e.key === "ArrowLeft"
              ? at - 1
              : e.key === "Home"
                ? 0
                : e.key === "End"
                  ? last
                  : null;
        if (to === null) return;
        e.preventDefault();
        go(to);
      }}
    >
      <div className="tm-strip-frame">
        <ul
          ref={list}
          className={`tm-strip-list${drag.dragging ? " is-dragging" : ""}`}
          onScroll={(e) => {
            if (!drag.moving() && easing.current === 0) setShowing(nearest(e.currentTarget));
          }}
          {...drag.handlers}
        >
          {photos.map((p, i) => (
            <StripPhoto
              key={keyOf(p)}
              photo={p}
              item={item}
              trip={trip}
              who={who}
              label={several ? `Photo ${i + 1} of ${photos.length}` : "Photo"}
              current={i === at}
              onOpen={() => setOpen(i)}
            />
          ))}
        </ul>
        {several && (
          <>
            <button
              ref={prev}
              type="button"
              className="tm-strip-step is-prev"
              aria-label="Previous photo"
              hidden={at === 0}
              onClick={() => go(at - 1)}
            >
              <Icon name="chevron" size={20} />
            </button>
            <button
              ref={next}
              type="button"
              className="tm-strip-step is-next"
              aria-label="Next photo"
              hidden={at === last}
              onClick={() => go(at + 1)}
            >
              <Icon name="chevron" size={20} />
            </button>
          </>
        )}
      </div>
      {several && (
        <div className="tm-strip-dots" role="status">
          <span className="tm-hidden">{`Photo ${Math.min(said, last) + 1} of ${photos.length}`}</span>
          {photos.map((p, i) => (
            <span key={keyOf(p)} className={`tm-strip-dot${i === at ? " is-current" : ""}`} aria-hidden="true" />
          ))}
        </div>
      )}
      {open !== null && (
        <PhotoViewer
          photos={photos}
          start={open}
          item={item}
          trip={trip}
          who={who}
          onClose={() => setOpen(null)}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

// One photo on the strip. Only the one showing is a stop for Tab (the arrow keys go through the rest).
function StripPhoto(props: {
  photo: Shown;
  item: Item;
  trip: string;
  who: Who;
  label: string;
  current: boolean;
  onOpen: () => void;
}) {
  const { photo, item, trip, who, label, current, onOpen } = props;
  const [state, setState] = useState<Load>("loading");
  // About the widest a card gets; on a phone, the screen's width.
  const width = Math.min(globalThis.innerWidth || 480, 480);
  const tab = current ? 0 : -1;
  return (
    <li className={`tm-strip-photo is-${state}`}>
      <button
        type="button"
        className="tm-strip-open"
        aria-label={`${label}, full screen`}
        tabIndex={tab}
        disabled={state === "failed"}
        onClick={onOpen}
      >
        <KindIcon item={item} />
        <PhotoImage photo={photo} item={item} trip={trip} width={width} className="tm-strip-img" onState={setState} />
      </button>
      {state !== "failed" &&
        ("own" in photo ? (
          <span className="caption tm-strip-credit tm-strip-by">
            <Icon name="camera" size={14} />
            <span className="tm-credit-text">{capital(who(photo.own.by))}</span>
          </span>
        ) : (
          <Credit photo={photo.web} className="tm-strip-credit" tabIndex={tab} />
        ))}
    </li>
  );
}

// Who to credit, opening the page the photo is from.
function Credit({ photo, className, tabIndex }: { photo: Photo; className: string; tabIndex?: number }) {
  return (
    <a
      className={`caption ${className}`}
      {...(tabIndex !== undefined ? { tabIndex } : {})}
      href={photo.source}
      target="_blank"
      rel="noreferrer noopener"
      title={photo.source}
    >
      <span className="tm-credit-text">{photo.credit}</span>
    </a>
  );
}

// Photos full screen, on black, one at a time: swipe, the arrows or the arrow keys go through them;
// Escape or Close returns. Drawn on the page itself, above everything. Below each: a web photo's credit,
// or who added an own photo and when, with Remove.
function PhotoViewer({
  photos,
  start,
  item,
  trip,
  who,
  onClose,
  onChanged,
}: {
  photos: Shown[];
  start: number;
  item: Item;
  trip: string;
  who: Who;
  onClose: () => void;
  onChanged: () => void;
}) {
  const track = useRef<HTMLUListElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(start);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = (i: number, smooth = true) => {
    const el = track.current;
    if (!el) return;
    const next = Math.max(0, Math.min(photos.length - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: smooth ? "smooth" : "instant" });
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: opens at the photo tapped, once
  useEffect(() => go(start, false), []);
  // Focus goes into the viewer while it's open, and back to where it was (the photo opened) after.
  useEffect(() => {
    const before = document.activeElement;
    dialog.current?.focus({ preventScroll: true });
    return () => {
      if (before instanceof HTMLElement && before.isConnected) before.focus({ preventScroll: true });
    };
  }, []);
  const latest = useRef({ at, go, onClose });
  latest.current = { at, go, onClose };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const { at, go, onClose } = latest.current;
      if (e.key === "Escape") {
        // Only the viewer closes, not the card under it.
        e.stopImmediatePropagation();
        onClose();
      } else if (e.key === "ArrowRight") go(at + 1);
      else if (e.key === "ArrowLeft") go(at - 1);
    };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, []);
  const photo = photos[at] ?? photos[0];
  // Nothing comes off a read-only trip.
  const readOnly = useReadOnly();
  // Removing an own photo: undoable from Updates, like any file. The viewer closes on the card without it.
  const remove = async (file: TripFile) => {
    setRemoving(true);
    setError(null);
    try {
      await api.removeFile(trip, file.id);
      onClose();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove it. Try again.");
      setRemoving(false);
    }
  };
  return createPortal(
    <div
      ref={dialog}
      className="tm-photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Photos of ${item.title}`}
      tabIndex={-1}
    >
      <ul
        ref={track}
        className="tm-photo-track"
        onScroll={(e) => {
          setAt(Math.round(e.currentTarget.scrollLeft / Math.max(1, e.currentTarget.clientWidth)));
          setError(null);
        }}
      >
        {photos.map((p, i) => (
          <li key={keyOf(p)} className="tm-photo-slide" aria-hidden={i !== at}>
            <FullPhoto photo={p} item={item} trip={trip} who={who} />
          </li>
        ))}
      </ul>
      <button type="button" className="tm-viewer-close" onClick={onClose}>
        <Icon name="close" size={20} />
        <span>Close</span>
      </button>
      {photos.length > 1 && (
        <>
          <ViewerArrow label="Previous photo" icon="prev" disabled={at === 0} onClick={() => go(at - 1)} />
          <ViewerArrow label="Next photo" icon="next" disabled={at === photos.length - 1} onClick={() => go(at + 1)} />
        </>
      )}
      {photo && (
        <div className="tm-photo-caption caption">
          {photos.length > 1 && (
            <>
              <span className="tm-photo-count">{`${at + 1} of ${photos.length}`}</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          {"own" in photo ? (
            <>
              <span className="tm-photo-by">{`Added by ${who(photo.own.by)}, ${ago(photo.own.at)}`}</span>
              {!readOnly && (
                <>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    className="tm-photo-remove"
                    disabled={removing}
                    onClick={() => remove(photo.own)}
                  >
                    {removing ? "Removing…" : "Remove"}
                  </button>
                </>
              )}
            </>
          ) : (
            <Credit photo={photo.web} className="tm-photo-credit" />
          )}
        </div>
      )}
      {error && (
        <p className="caption tm-photo-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>,
    document.body,
  );
}

function FullPhoto({ photo, item, trip, who }: { photo: Shown; item: Item; trip: string; who: Who }): ReactNode {
  const [failed, setFailed] = useState(false);
  const width = Math.max(globalThis.innerWidth || 0, globalThis.innerHeight || 0);
  if (failed)
    return (
      <span className="tm-photo-missing">
        <KindIcon item={item} size={28} />
        <span className="callout">
          {"own" in photo && !navigator.onLine ? "This photo isn't on this phone yet" : "This photo doesn't load"}
        </span>
      </span>
    );
  const alt =
    "own" in photo ? `${item.title}, added by ${who(photo.own.by)}` : `${item.title}, from ${photo.web.credit}`;
  return (
    <PhotoImage
      photo={photo}
      item={item}
      trip={trip}
      width={width}
      className="tm-photo-full"
      alt={alt}
      onState={(s) => s === "failed" && setFailed(true)}
    />
  );
}

function ViewerArrow(props: { label: string; icon: "prev" | "next"; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`tm-photo-arrow is-${props.icon}`}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <Icon name="chevron" size={22} />
    </button>
  );
}
